import NGHeading from '@NGComponents/NGHeading';
import NGIcon from '@NGComponents/NGIcon';
import { useIsLoggedIn } from '@apis/queries/useIsLoggedIn/useIsLoggedIn';
import { pushURL } from '@functions/functions';
import Flex from '@layouts/Flex';
import useLogin from '@store/userStore/hooks/useLogin';
import useLogout from '@store/userStore/hooks/useLogout';
import { FC, useCallback, useEffect, useRef } from 'react';
import { EFlex } from 'src/styles/layouts/Flex/Flex.enum';
import NGButton from 'src/styles/theme/components/NGButton';
import { EIcon } from 'src/styles/theme/components/NGIcon/NGIcon.enum';
import NGText from 'src/styles/theme/components/NGText';
import { ETextAlign, ETextType } from 'src/styles/theme/components/NGText/NGText.enum';
import { EHTMLDisplay, ESize, ETextColor } from 'src/styles/theme/utils/enums';
import {
	NeutralButtonEx,
	StyledAccountTooltip,
	StyledAccountTooltipContainer,
	StyledArrow
} from './AccountTooltip.styles';
import { IAccountTooltip } from './AccountTooltip.type';
import { ACCOUNT_TOOLTIP_LINKS } from './AccountTooltip.variables';

const AccountTooltip: FC<IAccountTooltip> = ({
	children,
	isDisabled = false,
	isVisible = false,
	closeAction,
	...otherProps
}) => {
	const { isLoggedIn } = useIsLoggedIn();

	const { login } = useLogin();
	const { logout } = useLogout();

	const timeoutRef = useRef<NodeJS.Timeout>();
	const tooltipRef = useRef<any>();

	const onClickOutside = useCallback(
		(event: MouseEvent) => {
			const { target } = event;

			if (!isVisible) {
				return;
			}

			// close tooltip if mouse is outside more than a second
			clearTimeout(timeoutRef.current);

			timeoutRef.current = setTimeout(() => {
				const currentRef = tooltipRef?.current;

				if (currentRef && !currentRef?.contains(target as Node)) {
					closeAction();
				}
			}, 250);
		},
		[isVisible, closeAction]
	);

	const handleDisconnectWallet = useCallback(() => {
		logout();

		setTimeout(() => {
			closeAction();
		}, 1000);
	}, [closeAction, logout]);

	const handleLinkClick = (pathname: string, subpage: string) => () => {
		pushURL(pathname, subpage);
	};

	useEffect(() => {
		if (isVisible) {
			document.addEventListener('mouseover', onClickOutside);
		} else {
			document.removeEventListener('mouseover', onClickOutside);
		}

		return () => {
			document.removeEventListener('mouseover', onClickOutside);
		};
	}, [isVisible, onClickOutside]);

	return (
		<StyledAccountTooltip {...otherProps} ref={tooltipRef}>
			{isDisabled ? (
				children
			) : (
				<>
					{children}
					<StyledAccountTooltipContainer isVisible={isVisible}>
						<StyledArrow>
							<span />
						</StyledArrow>
						<Flex vertical={EFlex.center}>
							{!isLoggedIn ? (
								<NGButton
									dataCy='login-button'
									fullWidth
									size={ESize.s}
									onClick={login}
									startIcon={EIcon.github}
									iconSize={ESize.xs}
									iconColor={ETextColor.white}
								>
									Log in with GitHub
								</NGButton>
							) : (
								<NGHeading type={ETextType.h4} textAlign={ETextAlign.center}>
									Welcome Guardian!
								</NGHeading>
							)}
						</Flex>
						{isLoggedIn ? (
							<>
								<NGButton
									dataCy='logout-button'
									fullWidth
									size={ESize.s}
									onClick={handleDisconnectWallet}
									marginTop={ESize['2xs']}
								>
									Log out
								</NGButton>
								<Flex direction={EFlex.column} horizontal={EFlex.start} marginTop={ESize.m}>
									{ACCOUNT_TOOLTIP_LINKS.map(({ id, dataCy, pathname, subpage, iconType, label }) => (
										<NeutralButtonEx
											key={id}
											data-cy={dataCy}
											display={EHTMLDisplay.flex}
											vertical={EFlex.center}
											padding={ESize.unset}
											onClick={handleLinkClick(pathname, subpage)}
										>
											<NGIcon
												type={iconType}
												size={ESize.s}
												color={ETextColor.gold}
												height={ESize.xs}
												width={ESize.xs}
											/>
											<NGText size={ESize.m} marginLeft={ESize['2xs']}>
												{label}
											</NGText>
										</NeutralButtonEx>
									))}
								</Flex>
							</>
						) : null}
					</StyledAccountTooltipContainer>
				</>
			)}
		</StyledAccountTooltip>
	);
};

export { AccountTooltip };
